import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Alert, Spinner, Card } from 'react-bootstrap';
import { BlobServiceClient } from '@azure/storage-blob';
import { renderAsync as renderDocx } from 'docx-preview';
import * as XLSX from 'xlsx';
import { storageConfig } from '../config/authConfig';

interface UserFileViewerProps {
  show: boolean;
  onHide: () => void;
  containerName: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}

const UserFileViewer: React.FC<UserFileViewerProps> = ({
  show,
  onHide,
  containerName,
  fileName,
  contentType,
  fileSize
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [excelHtml, setExcelHtml] = useState<string | null>(null);
  const [officeBuffer, setOfficeBuffer] = useState<ArrayBuffer | null>(null);
  const docxRef = useRef<HTMLDivElement | null>(null);
  const renderedBufferRef = useRef<ArrayBuffer | null>(null);

  const fileExt = (): string => (fileName.split('.').pop() || '').toLowerCase();
  const isHtmlFile = (): boolean => contentType.includes('html') || ['html', 'htm'].includes(fileExt());
  const isWordFile = (): boolean => contentType.includes('wordprocessingml') || fileExt() === 'docx';
  const isExcelFile = (): boolean =>
    contentType.includes('spreadsheetml') ||
    contentType === 'application/vnd.ms-excel' ||
    ['xlsx', 'xls'].includes(fileExt());

  // Render a .docx into the container as soon as both the bytes and the DOM node
  // are available (callback ref avoids a mount/timing race).
  const docxCallbackRef = useCallback((node: HTMLDivElement | null) => {
    docxRef.current = node;
    if (node && officeBuffer && isWordFile() && renderedBufferRef.current !== officeBuffer) {
      renderedBufferRef.current = officeBuffer;
      node.innerHTML = '';
      renderDocx(officeBuffer, node, undefined, { inWrapper: true, className: 'docx-preview' })
        .catch((e) => { console.error('Docx render error:', e); });
    }
  }, [officeBuffer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up blob URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  // Load file content when modal opens
  useEffect(() => {
    if (show && fileName && containerName) {
      loadFileContent();
    }
  }, [show, fileName, containerName]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFileContent = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    setFileContent(null);
    setExcelHtml(null);
    setOfficeBuffer(null);
    renderedBufferRef.current = null;

    // Clean up previous blob URL
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }

    try {
      // Get user's JWT token
      const token = localStorage.getItem('userAuthToken');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      // Get SAS token for reading
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      const sasResponse = await fetch(`${functionUrl}/generate_user_sas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          containerName: containerName,
          expiresInHours: 1
        })
      });
      
      if (!sasResponse.ok) {
        throw new Error('Failed to authenticate file access');
      }
      
      const { sasToken } = await sasResponse.json();
      
      // Use SAS token to download blob
      const accountUrl = `https://${storageConfig.accountName}.blob.core.windows.net`;
      const containerUrl = `${accountUrl}/${containerName}?${sasToken}`;
      const blobServiceClient = new BlobServiceClient(containerUrl);
      const containerClient = blobServiceClient.getContainerClient('');
      
      // fileName already contains the full path (including folder structure)
      const blobClient = containerClient.getBlobClient(fileName);
      
      const downloadResponse = await blobClient.download();
      let dataBlob: Blob | null = null;

      if (downloadResponse.blobBody) {
        dataBlob = await downloadResponse.blobBody;
      } else if (downloadResponse.readableStreamBody) {
        const webStream = downloadResponse.readableStreamBody as unknown as ReadableStream;
        const resp = new Response(webStream as any);
        dataBlob = await resp.blob();
      }

      if (!dataBlob) {
        throw new Error('Failed to load file content');
      }
      
      // Force the right MIME type on the preview blob so the browser renders it
      // inline instead of downloading it (PDFs and HTML in particular fail to
      // preview when the stored MIME type is generic/octet-stream).
      const previewType = isHtmlFile() ? 'text/html' : isPdfFile(contentType) ? 'application/pdf' : contentType;
      const typedBlob = new Blob([dataBlob], { type: previewType });
      
      // Create blob URL for the file
      const url = URL.createObjectURL(typedBlob);
      setBlobUrl(url);

      // For text files, read the content as text
      if (isTextFile(contentType)) {
        const text = await typedBlob.text();
        setFileContent(text);
      }

      // Office documents: render in-browser (bytes never leave the tenant).
      if (isWordFile() || isExcelFile()) {
        const buf = await typedBlob.arrayBuffer();
        if (isExcelFile()) {
          try {
            const wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
            const html = wb.SheetNames.map((n) =>
              `<div class="xlsx-sheet-name">${n}</div>${XLSX.utils.sheet_to_html(wb.Sheets[n])}`
            ).join('');
            setExcelHtml(html);
          } catch (e) {
            console.error('Excel render error:', e);
            setError('Could not render this spreadsheet. You can download it instead.');
          }
        } else {
          setOfficeBuffer(buf); // rendered by the docx callback ref
        }
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load file content. Please try again.';
      
      // Check for authorization errors
      if (errorMessage.includes('not authorized') || errorMessage.includes('Authentication required')) {
        setError('Authorization error: ' + errorMessage);
      } else if (errorMessage.includes('not found') || err.statusCode === 404) {
        setError('File not found. It may have been deleted.');
      } else {
        setError(errorMessage);
      }
      
      console.error('Error loading file:', err);
    } finally {
      setLoading(false);
    }
  };

  const isImageFile = (type: string): boolean => {
    return type.startsWith('image/');
  };

  const isTextFile = (type: string): boolean => {
    const normalized = (type || '').toLowerCase();

    // Exclude Office types and HTML (HTML is rendered, not shown as source)
    if (
      normalized.includes('html') ||
      normalized.includes('officedocument') ||
      normalized === 'application/msword' ||
      normalized === 'application/vnd.ms-excel' ||
      normalized === 'application/vnd.ms-powerpoint'
    ) {
      return false;
    }

    return (
      normalized.startsWith('text/') ||
      normalized === 'application/json' ||
      normalized === 'application/xml' ||
      normalized === 'text/xml' ||
      normalized.endsWith('+json') ||
      normalized.endsWith('+xml') ||
      normalized.includes('javascript') ||
      normalized.includes('typescript') ||
      normalized.includes('css') ||
      normalized.includes('markdown')
    );
  };

  const isPdfFile = (type: string): boolean => {
    return type === 'application/pdf' || fileExt() === 'pdf';
  };

  const isVideoFile = (type: string): boolean => {
    return type.startsWith('video/');
  };

  const isAudioFile = (type: string): boolean => {
    return type.startsWith('audio/');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownload = (): void => {
    if (blobUrl) {
      const link = document.createElement('a');
      link.href = blobUrl;
      // Extract just the file name from the full path
      const lastSlashIndex = fileName.lastIndexOf('/');
      const downloadName = lastSlashIndex >= 0 ? fileName.substring(lastSlashIndex + 1) : fileName;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const renderFilePreview = (): JSX.Element => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading file content...</p>
        </div>
      );
    }

    if (error) {
      return <Alert variant="danger">{error}</Alert>;
    }

    if (!blobUrl) {
      return <Alert variant="warning">No file content available</Alert>;
    }

    // HTML files — rendered as a page in a sandboxed frame (scripts disabled for
    // safety, since these are user-uploaded documents).
    if (isHtmlFile()) {
      return (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">Rendered preview — scripts are disabled for safety.</small>
            <Button variant="outline-primary" size="sm" onClick={() => window.open(blobUrl!, '_blank')}>
              <i className="bi bi-box-arrow-up-right me-1"></i>
              Open in new tab
            </Button>
          </div>
          <iframe
            src={blobUrl}
            title={fileName}
            sandbox=""
            width="100%"
            style={{ height: '70vh', border: '1px solid #dee2e6', borderRadius: '0.375rem', background: '#fff' }}
          />
        </div>
      );
    }

    // Image files
    if (isImageFile(contentType)) {
      return (
        <div className="text-center">
          <img
            src={blobUrl}
            alt={fileName}
            style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}
            className="img-fluid"
          />
        </div>
      );
    }

    // Text files
    if (isTextFile(contentType) && fileContent) {
      return (
        <Card>
          <Card.Body>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                maxHeight: '60vh',
                overflow: 'auto',
                fontSize: '0.875rem',
                backgroundColor: '#f8f9fa',
                padding: '1rem',
                borderRadius: '0.375rem'
              }}
            >
              {fileContent}
            </pre>
          </Card.Body>
        </Card>
      );
    }

    // PDF files — <object> renders reliably across browsers; falls back to an
    // iframe, then to an "open in a new tab" link if inline viewing is blocked.
    if (isPdfFile(contentType)) {
      return (
        <div>
          <div className="d-flex justify-content-end mb-2">
            <Button variant="outline-primary" size="sm" onClick={() => window.open(blobUrl!, '_blank')}>
              <i className="bi bi-box-arrow-up-right me-1"></i>
              Open in new tab
            </Button>
          </div>
          <iframe
            src={blobUrl!}
            title={fileName}
            width="100%"
            style={{ height: '70vh', border: 'none', borderRadius: '0.375rem' }}
          />
          <p className="text-muted mt-2 mb-0 small">
            If the PDF doesn't appear above,{' '}
            <a href={blobUrl!} target="_blank" rel="noreferrer">open it in a new tab</a>.
          </p>
        </div>
      );
    }

    // Video files
    if (isVideoFile(contentType)) {
      return (
        <div className="text-center">
          <video
            controls
            style={{ maxWidth: '100%', maxHeight: '60vh' }}
            className="img-fluid"
          >
            <source src={blobUrl} type={contentType} />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    // Audio files
    if (isAudioFile(contentType)) {
      return (
        <div className="text-center">
          <audio controls style={{ width: '100%' }}>
            <source src={blobUrl} type={contentType} />
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    }

    // Word documents (.docx) — rendered in-browser
    if (isWordFile()) {
      return (
        <div
          ref={docxCallbackRef}
          className="docx-viewer"
          style={{ maxHeight: '70vh', overflow: 'auto', background: '#fff', padding: '0.5rem' }}
        />
      );
    }

    // Excel spreadsheets (.xlsx/.xls) — rendered in-browser as tables
    if (isExcelFile() && excelHtml) {
      return (
        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <style>{`
            .xlsx-wrap table { border-collapse: collapse; font-size: 0.8rem; margin-bottom: 1rem; }
            .xlsx-wrap td, .xlsx-wrap th { border: 1px solid #dee2e6; padding: 2px 8px; white-space: nowrap; }
            .xlsx-sheet-name { font-weight: 600; margin: 0.75rem 0 0.25rem; color: #456b7a; }
          `}</style>
          <div className="xlsx-wrap" dangerouslySetInnerHTML={{ __html: excelHtml }} />
        </div>
      );
    }

    // Unsupported file types
    return (
      <Alert variant="info">
        <Alert.Heading>File Preview Not Available</Alert.Heading>
        <p>
          This file type ({contentType}) cannot be previewed in the browser.
          You can download the file to view it with an appropriate application.
        </p>
        <Button variant="primary" onClick={handleDownload}>
          Download File
        </Button>
      </Alert>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {fileName.includes('/') ? fileName.substring(fileName.lastIndexOf('/') + 1) : fileName}
            </div>
            <small className="text-muted">
              {formatFileSize(fileSize)} • {contentType}
              {fileName.includes('/') && (
                <> • <span className="text-info">{fileName.substring(0, fileName.lastIndexOf('/'))}</span></>
              )}
            </small>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '80vh', overflow: 'auto' }}>
        {renderFilePreview()}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        {blobUrl && (
          <Button variant="primary" onClick={handleDownload}>
            <i className="bi bi-download me-2"></i>
            Download
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default UserFileViewer;

